import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PageHeader from "../components/PageHeader";
import infoCircle from "../assets/info-circle.svg";
import TagInput from "../components/TagInput";
import { requestWithToken } from "../utils/apis"
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import moment from "moment";
import MediaPickerModal from "../components/MediaPickerModal";
import OGFields from "../components/pages/OGTagsFields";
import { fetchBlogCategoryList, saveBlogCategory } from "../action/formaction";
import TinyMceEditor from "../components/TinyMceEditor";
import LanguageSpecific from "../components/languageSpecific";
import { useSearchParams } from "react-router-dom";
import { fetchLanguageList } from "../action/page";
import { Dropdown } from "react-bootstrap";
import CommonModal from "../components/CommonModal";
import cross from "../assets/black-cross.svg";

const AddBlogs = () => {
  const navigate = useNavigate();
  const { blogId } = useParams();
  const [searchParams] = useSearchParams();
  const selectedLang = searchParams.get("lang");
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [banner, setBanner] = useState(null); // for selected banner
  const rightSectionRef = useRef(null);
  const [showOGMediaPicker, setShowOGMediaPicker] = useState(false);
  const [categories, setCategories] = useState([]);
  const editorRef = useRef(null);
  const [blogData, setBlogData] = useState({});
  const [authorImage, setAuthorImage] = useState(null);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categoryThumbnail, setCategoryThumbnail] = useState(null);
  const [showCategoryMediaPicker, setShowCategoryMediaPicker] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);

  const loadLists = async () => {
    const l = await fetchLanguageList();
    setLanguages(l?.data?.list || []);
  };

  const formik = useFormik({
    initialValues: {
      title: "",
      slug: "",
      author: "",
      content: "",
      focus_keywords: [],
      metaTitle: "",
      metaDesc: "",
      is_published: true,
      banner: "",
      desc: "",
      og_tags: {
        title: "",
        desc: "",
        url: "",
        image: ""
      },
      publish_date: moment().format("YYYY-MM-DD"),
      extras: {
        author_image: ""
      },
      blog_category_id: "",
      locale: "en"
    },
    validationSchema: Yup.object({
      // locale: Yup.string().required("Please select language"),
      title: Yup.string().required("Blog Title is required"),
      slug: Yup.string()
        .required("Slug is required")
        .matches(
          /^[a-zA-Z0-9\u0900-\u097F_-]+$/,
          "Slug can contain Hindi, English letters, numbers, hyphens, and underscores"
        ),
      content: Yup.string().required("Blog Content is required"),
      // focus_keywords: Yup.array().min(1, "At least one keyword is required"),
      // metaTitle: Yup.string().required("SEO Title is required"),
      // metaDesc: Yup.string().required("Meta Description is required"),
      banner: Yup.string().required("Banner image is required"),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const bannerpath = banner
          ? banner.replace(process.env.REACT_APP_BUCKET_URL, "")
          : null;

        const payload = {
          title: values.title,
          slug: values.slug,
          content: values.content,
          author_name: values.author,
          seo: {
            focus_keywords: values.focus_keywords.join(","),
            metaTitle: values.metaTitle,
            metaDesc: values.metaDesc,
          },
          assets: banner
            ? {
              type: 1, // 1 = image
              url: bannerpath,
              w: "0",
              h: "0",
            }
            : {},

          is_published: values.is_published,
          publish_date: moment(values.publish_date).toISOString(),
          desc: values.desc,
          type: 1,
          og_tags: values.og_tags,
          // blog_category_id:values.blog_category_id,
          extras: values.extras,
          locale: values.locale || "en"
        };

        if (values.blog_category_id) {
          payload.blog_category_id = values.blog_category_id;
        }

        // if(!values.is_published){
        //   delete payload.publish_date
        // }
        let response = null;
        if (blogId) {
          response = await requestWithToken(
            "PUT",
            `/api/blogs/${blogId}`,
            payload
          );
        } else {
          response = await requestWithToken("POST", "/api/blogs", payload);
        }

        if (response?.status === 200) {
          toast.success(blogId ? "Blog Updated Successfully!" : "Blog Created Successfully!");
          navigate("/blogs");
          resetForm();
          editorRef.current?.setContent("");
        }
      } catch (error) {
        console.error("Error submitting blog:", error);
        toast.error(
          error?.response?.data?.message ||
          "Something went wrong. Please try again."
        );
        if (loadingButton === "toggle") {
          // revert the toggle change if error occurs during publish toggle
          formik.setFieldValue("is_published", !formik?.values?.is_published);
        }
      } finally {
        setSubmitting(false);
        setLoadingButton(null)
      }
    },
  });

  useLayoutEffect(() => {
    if (!rightSectionRef.current) return;

    const element = rightSectionRef.current;

    const updateHeight = () => {
      const height = element.offsetHeight;
      document.documentElement.style.setProperty(
        "--right-section-height",
        `${height}px`
      );
    };

    updateHeight();

    const ro = new ResizeObserver(updateHeight);
    ro.observe(element);

    return () => ro.disconnect();
  }, [formik.values.content]);


  // Fetch blog details in edit mode
  const fetchDetails = async (id) => {
    try {
      const response = await requestWithToken("GET", `/api/blogs/${id}`);
      if (response?.status === 200) {
        const blog = response.data.data;
        setBlogData(blog);
        formik.setValues({
          title: blog.title || "",
          slug: blog.slug || "",
          content: blog.content || "",
          author: blog.author_name || "",
          focus_keywords: blog.seo?.focus_keywords
            ? blog.seo.focus_keywords.split(",")
            : [],
          metaTitle: blog.seo?.metaTitle || "",
          metaDesc: blog.seo?.metaDesc || "",
          is_published: blog.is_published || false,
          banner: blog.assets?.url ? process.env.REACT_APP_BUCKET_URL + blog.assets.url : "",
          desc: blog?.desc || "",
          og_tags: blog?.og_tags,
          publish_date: moment(blog?.publish_date).format("YYYY-MM-DD") || "",
          extras: blog?.extras || {},
          blog_category_id: blog?.blog_category?._id || "",
          locale: blog?.locale || "",
          parent_id: blog?.parent_id || null
        });

        formik.setFieldValue("content", blog.content || "");
        setBanner(blog.assets?.url ? process.env.REACT_APP_BUCKET_URL + blog.assets.url : null);
        setAuthorImage(blog?.extras?.author_image ? process.env.REACT_APP_BUCKET_URL + blog.extras?.author_image : null)
      }
    } catch (error) {
      console.error("Error fetching blog details:", error);
      toast.error("Failed to fetch blog details");
    }
  };

  // useEffect(() => {
  //   if (blogId) {
  //     fetchDetails(blogId);
  //   }
  // }, [blogId]);
  useEffect(() => {
    // Only fetch existing blog when NOT creating localized version
    if (blogId && !selectedLang) {
      fetchDetails(blogId);
    }
  }, [blogId, selectedLang]);

  const [loadingButton, setLoadingButton] = useState(null);

  const handlePublishToggle = async () => {
    setLoadingButton("toggle");
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      // No validation errors → safe to toggle publish
      formik.setFieldValue("is_published", !formik?.values?.is_published);
      await formik.submitForm(); // same as handleSubmit but returns a Promise
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      setLoadingButton(null); // stop loader if invalid
    }
  };

  const handleSaveOrUpdate = async () => {
    setLoadingButton("save");
    const errors = await formik.validateForm();

    if (Object.keys(errors).length === 0) {
      await formik.submitForm();
    } else {
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {})
      );
      setLoadingButton(null); // stop loader if invalid
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetchBlogCategoryList(1, 100);
      if (response?.status) {
        const catList = response?.data?.list || [];
        setCategories(catList);
        if (!blogId && catList.length > 0 && !formik.values.blog_category_id) {
          formik.setFieldValue("blog_category_id", catList[0]._id);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch blog categories");
    }
  };

  useEffect(() => {
    fetchCategories();
    loadLists();
  }, []);

  const handleAddCategory = async () => {
    if (!categoryName) {
      toast.error("Please enter category name");
      return;
    }
    setSavingCategory(true);
    try {
      const thumbnailPath = categoryThumbnail
        ? categoryThumbnail.replace(process.env.REACT_APP_BUCKET_URL, "")
        : "";
      const payload = {
        name: categoryName,
        thumbnail: thumbnailPath,
      };
      const res = await saveBlogCategory(payload);
      if (res?.status) {
        toast.success("Category added successfully!");
        handleCloseCategoryModal();
        fetchCategories();
      } else {
        toast.error(res?.message || "Failed to add category");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setSavingCategory(false);
    }
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setCategoryName("");
    setCategoryThumbnail(null);
  };

  const filteredCategories = categories.filter((c) =>
    c.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  useEffect(() => {
    if (selectedLang) {
      // 🧹 Reset form for new localized blog
      formik.resetForm();
      setBanner(null);
      setBlogData({});
    }
  }, [selectedLang]);

  return (
    <>
      {/* <PageHeader pageTitle={blogId ? "Blog Details" : "Add New Blog"}> */}
      <PageHeader
        pageTitle={
          selectedLang
            ? `Add ${selectedLang.toUpperCase()} Blog`
            : blogId
              ? "Blog Details"
              : "Add New Blog"
        }
      >
        <button
          className="btn-white"
          onClick={handlePublishToggle}
          disabled={loadingButton === "toggle" && formik.isSubmitting}
        >
          {loadingButton === "toggle" && formik.isSubmitting ? (
            <Spinner size="sm" />
          ) : formik?.values?.is_published ? (
            blogId ? (
              "Unpublish"
            ) : (
              "Save Draft"
            )
          ) : (
            "Publish"
          )}
        </button>

        <button
          className="btn-black"
          onClick={handleSaveOrUpdate}
          disabled={loadingButton === "save" && formik.isSubmitting}
        >
          {loadingButton === "save" && formik.isSubmitting ? (
            <Spinner size="sm" />
          ) : blogId ? (
            "Update"
          ) : (
            "Publish"
          )}
        </button>
      </PageHeader>

      <div className="page-container">
        <div className="add-blog-form">
          <div className="row">
            {/* Left Section */}
            <div className="col-md-8 d-flex flex-column">
              <div className="theme-card text-card p-3 mb-3 h-100 ">
                {/* Blog Content (TinyEditor) */}
                <div className="frm-group h-100 d-flex flex-column mb-0 text-editor-group">
                  <div className="head-title">
                    <label className="frm-label d-flex align-items-center">
                      Blog Content
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Blog Description"
                      />
                    </label>
                  </div>
                  <div className="tinymce-wrapper">
                    <TinyMceEditor
                      value={formik.values.content}
                      onChange={(content) =>
                        formik.setFieldValue("content", content)
                      }
                      editorRef={editorRef}
                      height="100%"
                    />
                  </div>

                  {formik.touched.content && formik.errors.content && (
                    <div className="input-error">{formik.errors.content}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Section */}
            <div className="col-md-4 position-relative">
              <div className="right-sec" ref={rightSectionRef}>
                {/* General Details */}
                <div className="theme-card p-4 mb-3">
                  <div className="card-title pb-3">General Details</div>
                  {/* <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Blog Language
                    </label>
                    <div className="sel-box ms-auto">
                      <select
                        name="locale"
                        id="selmenu"
                        className="theme-select"
                        onChange={formik.handleChange}
                        value={formik.values?.locale || ""}
                      >
                        { languages.length > 0 ? (
                          <>
                            <option value="">Select Language</option>
                            {languages.map((c) => (
                              <option key={c._id} value={c.slug}>
                                {c.name}
                              </option>
                            ))}
                          </>
                        ) : (
                          <option>No Language Found</option>
                        )}
                      </select>
                      
                    </div>
                  </div> */}
                  {/* Blog Title */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Blog Title
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Blog Title"
                      />
                    </label>
                    <input
                      type="text"
                      name="title"
                      className="frm-input"
                      placeholder="Enter Blog Title"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.title}
                    />
                    {formik.touched.title && formik.errors.title && (
                      <div className="input-error">{formik.errors.title}</div>
                    )}
                  </div>

                  {/* Slug */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Slug
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Slug name of Blog"
                      />
                    </label>
                    <input
                      type="text"
                      name="slug"
                      className="frm-input"
                      placeholder="Slug name eg. my-first-blog"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.slug}
                      disabled={formik.values.locale !== "en"}
                    />
                    {formik.touched.slug && formik.errors.slug && (
                      <div className="input-error">{formik.errors.slug}</div>
                    )}
                  </div>
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Author Name
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Author name of Blog"
                      />
                    </label>
                    <input
                      type="text"
                      name="author"
                      className="frm-input"
                      placeholder="Author Name eg. John Doe"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.author}
                    />
                    {formik.touched.author && formik.errors.author && (
                      <div className="input-error">{formik.errors.author}</div>
                    )}
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">Author Image</label>

                    <div
                      className="pointer"
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowImagePicker(true)}
                    >
                      {authorImage ? (
                        <img
                          src={authorImage}
                          alt="image preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "12px",
                            background: "#f5f5f5",
                            color: "#777",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                        >
                          Upload Image
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Publish Date
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Select publish date"
                      />
                    </label>
                    <input
                      type="date"
                      name="publish_date"
                      className="frm-input"
                      value={formik.values.publish_date}
                      onChange={formik.handleChange}
                      onKeyDown={(e) => e.preventDefault()}
                    />
                  </div>
                  <div className="frm-group">
                    <label className="frm-label">Blog Banner</label>

                    <div
                      className="pointer"
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        cursor: "pointer",
                      }}
                      onClick={() => setShowMediaPicker(true)}
                    >
                      {banner ? (
                        <img
                          src={banner}
                          alt="banner preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "12px",
                            background: "#f5f5f5",
                            color: "#777",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                          }}
                        >
                          Upload Banner
                        </div>
                      )}
                    </div>
                    {formik.touched.banner && formik.errors.banner && (
                      <div className="input-error">{formik.errors.banner}</div>
                    )}
                  </div>
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Description
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add description of Blog"
                      />
                    </label>
                    <input
                      type="text"
                      name="desc"
                      className="frm-input"
                      placeholder="Enter Description"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.desc}
                    />
                  </div>

                  {/* blog category */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Blog Category
                      <button
                        type="button"
                        className="btn btn-sm btn-black ms-auto py-0 px-2"
                        onClick={() => setShowCategoryModal(true)}
                        style={{ height: "24px", fontSize: "16px" }}
                      >
                        +
                      </button>
                    </label>
                    <div className="sel-box">
                      <Dropdown className="w-100 theme-dropdown">
                        <Dropdown.Toggle
                          variant="none"
                          className="theme-select w-100 text-start d-flex justify-content-between align-items-center"
                        >
                          {(() => {
                            const selectedCat = categories.find(
                              (c) => c._id === formik.values.blog_category_id
                            );
                            if (!selectedCat) return "Select Category";
                            const thumb = selectedCat.thumbnail || selectedCat.extras?.thumbnail;
                            return (
                              <div className="d-flex align-items-center gap-2">
                                {thumb && (
                                  <img
                                    src={process.env.REACT_APP_BUCKET_URL + thumb}
                                    alt=""
                                    style={{ width: "20px", height: "20px", borderRadius: "4px", objectFit: "cover" }}
                                  />
                                )}
                                {selectedCat.name}
                              </div>
                            );
                          })()}
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="w-100 p-2">
                          <input
                            type="text"
                            className="frm-input mb-2"
                            placeholder="Search category..."
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div
                            style={{ maxHeight: "200px", overflowY: "auto" }}
                          >
                            {filteredCategories.length > 0 ? (
                              filteredCategories.map((c) => (
                                <Dropdown.Item
                                  key={c._id}
                                  onClick={() => {
                                    formik.setFieldValue(
                                      "blog_category_id",
                                      c._id
                                    );
                                  }}
                                  className="d-flex align-items-center gap-2"
                                >
                                  <div style={{ width: "30px", height: "30px", borderRadius: "4px", overflow: "hidden", background: "#f5f5f5" }}>
                                    {(c.thumbnail || c.extras?.thumbnail) ? (
                                      <img
                                        src={process.env.REACT_APP_BUCKET_URL + (c.thumbnail || c.extras.thumbnail)}
                                        alt=""
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      />
                                    ) : null}
                                  </div>
                                  {c.name}
                                </Dropdown.Item>
                              ))
                            ) : (
                              <Dropdown.Item disabled>
                                No Category Found
                              </Dropdown.Item>
                            )}
                          </div>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                </div>
                {blogData && (
                  <LanguageSpecific
                    blogId={blogId}
                    blogData={blogData}
                    setBlogData={setBlogData}
                  />
                )}
                {/* SEO Section */}
                <div className="theme-card p-4 mb-3">
                  <div className="card-title pb-3">SEO</div>

                  {/* Focus Keywords */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Focus Keywords
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Keywords"
                      />
                    </label>
                    <div className="tag-card">
                      <TagInput
                        value={formik.values.focus_keywords}
                        onChange={(tags) =>
                          formik.setFieldValue("focus_keywords", tags)
                        }
                      />
                      {/* {formik.touched.focus_keywords &&
                        formik.errors.focus_keywords && (
                          <div className="input-error">
                            {formik.errors.focus_keywords}
                          </div>
                        )} */}
                    </div>
                  </div>

                  {/* SEO Title */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      SEO Title
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add SEO Title"
                      />
                    </label>
                    <input
                      type="text"
                      name="metaTitle"
                      className="frm-input"
                      placeholder="Enter SEO Title"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.metaTitle}
                    />
                    {/* {formik.touched.metaTitle && formik.errors.metaTitle && (
                      <div className="input-error">
                        {formik.errors.metaTitle}
                      </div>
                    )} */}
                  </div>

                  {/* Meta Description */}
                  <div className="frm-group">
                    <label className="frm-label d-flex align-items-center">
                      Meta Description
                      <img
                        className="ms-2"
                        src={infoCircle}
                        alt="Info"
                        title="Add Meta Description"
                      />
                    </label>
                    <textarea
                      name="metaDesc"
                      className="frm-input"
                      rows={3}
                      placeholder="Enter Meta Description"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.metaDesc}
                    />
                    {/* {formik.touched.metaDesc && formik.errors.metaDesc && (
                      <div className="input-error">
                        {formik.errors.metaDesc}
                      </div>
                    )} */}
                  </div>
                </div>

                <OGFields
                  values={formik.values}
                  errors={formik.errors}
                  touched={formik.touched}
                  handleChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  setFieldValue={formik.setFieldValue}
                  showMediaPicker={showOGMediaPicker}
                  setShowMediaPicker={setShowOGMediaPicker}
                />
              </div>
            </div>
            {/* End Right Section */}
          </div>
        </div>
      </div>
      {showMediaPicker && (
        <MediaPickerModal
          show={showMediaPicker}
          onClose={() => setShowMediaPicker(false)}
          onSelect={(mediaUrl) => {
            setBanner(mediaUrl);
            formik.setFieldValue("banner", mediaUrl);
            setShowMediaPicker(false);
          }}
        />
      )}
      {showImagePicker && (
        <MediaPickerModal
          show={showImagePicker}
          onClose={() => setShowImagePicker(false)}
          onSelect={(mediaUrl) => {
            const imagePath = mediaUrl.replace(
              process.env.REACT_APP_BUCKET_URL,
              ""
            );
            setAuthorImage(mediaUrl);
            formik.setFieldValue("extras.author_image", imagePath);
            setShowImagePicker(false);
          }}
        />
      )}

      {/* Add Category Modal */}
      <CommonModal
        show={showCategoryModal}
        onClose={handleCloseCategoryModal}
      >
        <div className="p-3">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0">Add New Category</h5>
            <img
              src={cross}
              alt="close"
              className="pointer"
              onClick={handleCloseCategoryModal}
            />
          </div>

          <div className="frm-group">
            <label className="frm-label">Category Name</label>
            <input
              type="text"
              className="frm-input"
              placeholder="Enter category name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
          </div>

          <div className="frm-group">
            <label className="frm-label">Category Thumbnail</label>
            <div
              className="pointer d-flex align-items-center justify-content-center"
              style={{
                width: "100%",
                height: "150px",
                borderRadius: "12px",
                background: "#f5f5f5",
                overflow: "hidden",
                border: "1px dashed #ccc",
              }}
              onClick={() => setShowCategoryMediaPicker(true)}
            >
              {categoryThumbnail ? (
                <img
                  src={categoryThumbnail}
                  alt="thumbnail"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <span style={{ color: "#777" }}>Upload Thumbnail</span>
              )}
            </div>
          </div>

          <div className="mt-4 d-flex gap-2">
            <button
              className="btn btn-white w-100"
              onClick={handleCloseCategoryModal}
            >
              Cancel
            </button>
            <button
              className="btn btn-black w-100"
              onClick={handleAddCategory}
              disabled={savingCategory}
            >
              {savingCategory ? <Spinner size="sm" /> : "Save Category"}
            </button>
          </div>
        </div>
      </CommonModal>

      {showCategoryMediaPicker && (
        <MediaPickerModal
          show={showCategoryMediaPicker}
          onClose={() => setShowCategoryMediaPicker(false)}
          onSelect={(url) => {
            setCategoryThumbnail(url);
            setShowCategoryMediaPicker(false);
          }}
        />
      )}
    </>
  );
};

export default AddBlogs;
